<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import type { CSSProperties } from 'vue'
import type { MediaAsset, SoftwareStyle } from '@bf/domain'
import { useShowroomStore } from '../store/showroom'

const store = useShowroomStore()
const route = useRoute()
const activePageKey = ref('')

const styles = computed(() =>
  store.entities.value
    .filter((entity): entity is SoftwareStyle => entity.contentType === 'software-style')
    .sort((left, right) => left.sortOrder - right.sortOrder)
)
const style = computed(() =>
  styles.value.find((item) => item.id === String(route.params.id)) ?? styles.value[0] ?? null
)
const selectedStyleId = computed(
  () => store.state.draft?.items.find((item) => item.contentType === 'software-style')?.entityId ?? ''
)
const isSelected = computed(() => style.value?.id === selectedStyleId.value)
const customerHighlights = computed(() =>
  style.value?.highlights.filter((highlight) => !highlight.includes('编号映射')) ?? []
)
const activePage = computed(() => {
  if (style.value === null) return null
  return (
    style.value.pagePreviews.find((page) => page.pageKey === activePageKey.value) ??
    style.value.pagePreviews[0] ??
    null
  )
})
const activeAsset = computed(() =>
  activePage.value === null ? undefined : imageAsset(activePage.value.mediaId)
)
const activePageIndex = computed(() => {
  if (style.value === null || activePage.value === null) return 0
  const index = style.value.pagePreviews.findIndex((page) => page.pageKey === activePage.value?.pageKey)
  return index < 0 ? 0 : index
})
const businessPageProgress = computed(() => {
  const total = style.value?.pagePreviews.length ?? 0
  return total === 0 ? '0%' : `${((activePageIndex.value + 1) / total) * 100}%`
})

watch(
  style,
  (current) => {
    activePageKey.value = current?.pagePreviews[0]?.pageKey ?? ''
  },
  { immediate: true }
)

function imageAsset(mediaId: string): MediaAsset | undefined {
  const asset = store.getAsset(mediaId)
  return asset?.mediaType === 'image' ? asset : undefined
}

function coverAsset(item: SoftwareStyle): MediaAsset | undefined {
  const previewId = item.pagePreviews[0]?.mediaId
  if (previewId !== undefined) {
    const preview = imageAsset(previewId)
    if (preview !== undefined) return preview
  }
  return item.mediaIds
    .map((id) => store.getAsset(id))
    .find((asset): asset is MediaAsset => asset?.mediaType === 'image')
}

function themeVars(item: SoftwareStyle): CSSProperties {
  return {
    '--style-background': item.palette.background,
    '--style-surface': item.palette.surface,
    '--style-primary': item.palette.primary,
    '--style-accent': item.palette.accent,
    '--style-text': item.palette.text
  } as CSSProperties
}

function selectStyle() {
  if (style.value !== null) store.addToDraft(style.value)
}

function customerPageLabel(label: string) {
  return label.replace(/（编号映射）/g, '')
}

function moveBusinessPage(direction: -1 | 1) {
  if (style.value === null) return
  const nextPage = style.value.pagePreviews[activePageIndex.value + direction]
  if (nextPage !== undefined) activePageKey.value = nextPage.pageKey
}
</script>

<template>
  <section v-if="style" class="software-demo software-template-page" :style="themeVars(style)">
    <div class="demo-header software-package-header">
      <div>
        <p class="eyebrow">8 COMPLETE SOFTWARE STYLE PACKAGES</p>
        <h2>{{ style.title }}</h2>
        <p>{{ style.summary }}</p>
      </div>
      <div class="demo-actions">
        <button
          class="button"
          :class="isSelected ? 'button-selected' : 'button-primary'"
          type="button"
          :aria-pressed="isSelected"
          @click="selectStyle"
        >
          {{ isSelected ? '✓ 当前方案已选' : '选定为客户方案风格' }}
        </button>
        <RouterLink class="button button-secondary" :to="{ name: 'catalog', params: { section: 'software' } }">
          返回风格列表
        </RouterLink>
      </div>
    </div>

    <div class="software-template-showcase">
      <aside class="software-template-switcher" aria-label="软件界面完整风格包">
        <header>
          <span>STYLE LIBRARY</span>
          <strong>8 套完整风格包 · 单选</strong>
          <small>每套均覆盖 10 个业务画面，用于逐页讲解软件功能</small>
        </header>
        <RouterLink
          v-for="(item, index) in styles"
          :key="item.id"
          :class="{ active: item.id === style.id, selected: item.id === selectedStyleId }"
          :to="{ name: 'software-demo', params: { id: item.id } }"
          :style="themeVars(item)"
        >
          <img v-if="coverAsset(item)" :src="store.mediaUrl(coverAsset(item)!)" :alt="coverAsset(item)!.altText" />
          <span>{{ String(index + 1).padStart(2, '0') }}</span>
          <div>
            <strong>{{ item.title }}</strong>
            <small>{{ item.themeLabel }}</small>
          </div>
          <b v-if="item.id === selectedStyleId">已选</b>
        </RouterLink>
      </aside>

      <article class="software-template-stage">
        <header>
          <div>
            <span>当前完整风格包</span>
            <strong>{{ style.themeLabel }}</strong>
            <small>10 个业务画面 · 逐页讲解软件功能</small>
          </div>
          <div class="software-package-status">
            <b>10 / 10 业务画面覆盖</b>
            <span v-if="isSelected">已用于本次方案</span>
          </div>
        </header>

        <p class="software-business-consistency">
          8 套风格的软件功能与业务流程保持一致，仅视觉语言不同；可按客户偏好选定风格，再沿 10 个业务画面完整介绍功能。
        </p>

        <nav class="software-page-selector" aria-label="10 个业务画面选择">
          <button
            v-for="(page, index) in style.pagePreviews"
            :key="page.pageKey"
            type="button"
            :class="{ active: page.pageKey === activePage?.pageKey }"
            :aria-pressed="page.pageKey === activePage?.pageKey"
            :aria-label="`第 ${index + 1} 个业务画面：${customerPageLabel(page.pageLabel)}`"
            @click="activePageKey = page.pageKey"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ customerPageLabel(page.pageLabel) }}</strong>
          </button>
        </nav>

        <div class="software-business-viewer">
          <figure class="software-page-preview">
            <img
              v-if="activeAsset && activePage"
              :src="store.mediaUrl(activeAsset)"
              :alt="`${style.title} · ${customerPageLabel(activePage.pageLabel)}业务画面预览`"
            />
            <div v-else class="media-fallback"><strong>UI</strong><span>业务画面预览待接入</span></div>
            <figcaption v-if="activePage">
              <span>{{ style.themeLabel }}</span>
              <strong>{{ customerPageLabel(activePage.pageLabel) }}</strong>
            </figcaption>
          </figure>

          <aside v-if="activePage" class="software-function-guide" aria-live="polite">
            <header>
              <span>BUSINESS VIEW {{ String(activePageIndex + 1).padStart(2, '0') }} / {{ style.pagePreviews.length }}</span>
              <h3>{{ customerPageLabel(activePage.pageLabel) }}</h3>
              <p>{{ activePage.customerIntro }}</p>
            </header>
            <section>
              <h4>核心功能</h4>
              <ul>
                <li v-for="capability in activePage.capabilities" :key="capability">
                  <i aria-hidden="true">✓</i>
                  <span>{{ capability }}</span>
                </li>
              </ul>
            </section>
            <section class="software-customer-value">
              <span>客户价值</span>
              <strong>{{ activePage.customerValue }}</strong>
            </section>
          </aside>
        </div>

        <div class="software-business-navigation">
          <button
            type="button"
            :disabled="activePageIndex === 0"
            aria-label="查看上一个业务画面"
            @click="moveBusinessPage(-1)"
          >
            <span aria-hidden="true">←</span> 上一业务面
          </button>
          <div
            class="software-business-progress"
            role="progressbar"
            aria-label="业务画面讲解进度"
            :aria-valuemin="1"
            :aria-valuemax="style.pagePreviews.length"
            :aria-valuenow="activePageIndex + 1"
          >
            <span><i :style="{ width: businessPageProgress }" /></span>
            <strong>第 {{ activePageIndex + 1 }} / {{ style.pagePreviews.length }} 个业务画面</strong>
          </div>
          <button
            type="button"
            :disabled="activePageIndex === style.pagePreviews.length - 1"
            aria-label="查看下一个业务画面"
            @click="moveBusinessPage(1)"
          >
            下一业务面 <span aria-hidden="true">→</span>
          </button>
        </div>

        <div class="software-package-summary">
          <section>
            <span>方案色板</span>
            <div class="software-palette" aria-label="当前风格配色">
              <i v-for="(color, name) in style.palette" :key="name" :style="{ background: color }" :title="`${name}: ${color}`" />
            </div>
          </section>
          <section>
            <span>推荐项目</span>
            <div class="software-recommendations">
              <i v-for="item in style.recommendedFor" :key="item">{{ item }}</i>
            </div>
          </section>
          <section>
            <span>风格亮点</span>
            <ul><li v-for="item in customerHighlights" :key="item">{{ item }}</li></ul>
          </section>
        </div>

        <div class="software-selection-footer">
          <div>
            <strong>{{ isSelected ? '当前方案已采用此风格' : '确认后将作为整套客户方案的统一界面风格' }}</strong>
            <span>再次选择其他风格时，会自动替换原有选择，不会重复加入。</span>
          </div>
          <button
            class="button"
            :class="isSelected ? 'button-selected' : 'button-primary'"
            type="button"
            :aria-pressed="isSelected"
            @click="selectStyle"
          >
            {{ isSelected ? '✓ 已选定' : '选定此完整风格包' }}
          </button>
        </div>

        <div class="software-demo-scope-note" role="note" aria-label="软件演示范围说明">
          <strong>演示范围说明</strong>
          <p>当前为软件功能与视觉方案演示，正式范围以设备清单、点表、通讯协议、控制策略、权限矩阵及实施文件为准。</p>
          <small>画面中的设备名称、状态、数值和曲线均为演示内容，不代表真实项目数据。</small>
        </div>
      </article>
    </div>
  </section>
</template>
